import { useEffect, useRef } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ThumbsUpDownOutlined from "@mui/icons-material/ThumbsUpDownOutlined";

import { LabeledAction } from "./LabeledAction";

/**
 * Header feedback affordance. Clicking it opens the public Nolt board at
 * https://white-hat-bots.nolt.io/ inside Nolt's slide-in sidebar (a new-tab
 * navigation is the fallback if the Nolt SDK failed to load — ad-blocker,
 * offline, or strict CSP).
 *
 * The Nolt SDK is loaded once via `<script defer>` in index.html. This
 * component initialises it against a hidden `<a data-nolt="modal">` element
 * the first time it mounts, then triggers that anchor on click.
 *
 * The previous Quick-feedback Dialog (thumbs up/down + textarea) was removed
 * in favour of Nolt so every piece of product feedback has a single home —
 * see HANDOFF.md for context.
 */

const NOLT_URL = "https://white-hat-bots.nolt.io";
const NOLT_TRIGGER_ID = "nolt-feedback-trigger";

declare global {
  interface Window {
    nolt?: (action: string, config?: Record<string, unknown>) => void;
  }
}

interface Props {
  /** When provided, renders a caption label under the icon (desktop only). */
  label?: string;
}

export function FeedbackButton({ label }: Props = {}) {
  const noltAnchorRef = useRef<HTMLAnchorElement | null>(null);
  const noltInitedRef = useRef(false);

  // Initialise Nolt once the SDK is present. The script is deferred in
  // index.html, so it may not be ready on first mount — retry a few
  // times with a short back-off, then give up and rely on the
  // new-tab fallback inside handleOpenNolt.
  useEffect(() => {
    if (noltInitedRef.current) return;

    let attempts = 0;
    const timers: number[] = [];

    const tryInit = () => {
      if (noltInitedRef.current) return;
      if (typeof window.nolt === "function") {
        try {
          window.nolt("init", {
            selector: `#${NOLT_TRIGGER_ID}`,
            url: NOLT_URL
          });
          noltInitedRef.current = true;
          return;
        } catch {
          /* Fall through to retry */
        }
      }
      if (attempts++ < 6) {
        timers.push(window.setTimeout(tryInit, 400));
      }
    };

    tryInit();
    return () => { timers.forEach(clearTimeout); };
  }, []);

  const handleOpenNolt = () => {
    if (typeof window.nolt === "function" && noltAnchorRef.current) {
      // Click the hidden anchor — Nolt's SDK intercepts this and opens
      // its board in the slide-in sidebar modal.
      noltAnchorRef.current.click();
    } else {
      // SDK didn't load (ad-blocker, offline, CSP). Open in a new tab.
      window.open(NOLT_URL, "_blank", "noopener,noreferrer");
    }
  };

  const trigger = (
    <Tooltip title="Feedback & feature requests">
      <IconButton onClick={handleOpenNolt} aria-label="Feedback">
        <ThumbsUpDownOutlined />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {label ? <LabeledAction label={label}>{trigger}</LabeledAction> : trigger}

      {/* Hidden Nolt trigger — the SDK attaches its click handler to this
       *  element via the `#nolt-feedback-trigger` selector. Kept mounted
       *  at all times so the button stays wired up. */}
      <a
        ref={noltAnchorRef}
        id={NOLT_TRIGGER_ID}
        href={NOLT_URL}
        data-nolt="modal"
        rel="noopener noreferrer"
        style={{ display: "none" }}
        aria-hidden
        tabIndex={-1}
      >
        Feedback
      </a>
    </>
  );
}
